import React, { ReactElement, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Card, Flex, Row, Statistic } from 'antd';
import {
  MessageOutlined,
  ReloadOutlined,
  ShoppingOutlined,
  ThunderboltOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { blue, cyan, magenta, orange, purple } from '@ant-design/colors';
import { Button, Space, Text } from '@g2u/ui-kit';

import { Section } from 'components';

import usePerformanceStats from '../../hooks/usePerformanceStats';
import styles from './todays-performance.module.scss';

export namespace TodaysPerformance {
  type PerformanceLabel = {
    dataKey: keyof ReturnType<typeof usePerformanceStats>[0];
    title: string;
    value: string;
    suffix: string;
    icon: ReactElement<any>;
    color: string;
    action?: ReactElement<any>;
  };

  const BookNewJobButton = () => {
    const navigate = useNavigate();

    return (
      <Button.$ type={Button.Type.Primary} dataTestId="book-new-job-button" onClick={() => navigate('/booking/start')}>
        Book a new job ➔
      </Button.$>
    );
  };

  const definitions: Omit<PerformanceLabel, 'value'>[] = [
    {
      dataKey: 'jobsPerHour',
      title: 'Average Jobs Booked',
      suffix: 'per hour',
      icon: <ShoppingOutlined />,
      color: blue[5],
      action: <BookNewJobButton />,
    },
    {
      dataKey: 'callsPerHour',
      title: 'Average Calls Handled',
      suffix: 'per hour',
      icon: <WhatsAppOutlined />,
      color: magenta[5],
    },
    {
      dataKey: 'avgCallDuration',
      title: 'Average Call Time',
      suffix: 'minute(s) per call',
      icon: <MessageOutlined />,
      color: cyan[5],
    },
    {
      dataKey: 'conversionRate',
      title: 'Conversion Rate',
      suffix: '',
      icon: <ReloadOutlined />,
      color: purple[5],
    },
    {
      dataKey: 'sdsStrikeRate',
      title: 'SDS Strike Rate',
      suffix: '',
      icon: <ThunderboltOutlined />,
      color: orange[5],
    },
  ];

  export const $: FC = () => {
    const [data, isLoading] = usePerformanceStats();

    return (
      <Section.$ full style={{ flex: 1 }}>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
          <Card.Meta title="Today's Performance" />
          {definitions.map(({ dataKey, title, suffix, icon, color, action }) => (
            <Card key={dataKey} type="inner">
              <Flex gap="middle">
                <Avatar icon={icon} shape="square" style={{ backgroundColor: color }} />
                <Statistic
                  title={title}
                  className={styles.performance__statistic}
                  suffix={
                    suffix ? (
                      <Text.$ type="secondary" size={12}>
                        {' '}
                        | {suffix}
                      </Text.$>
                    ) : undefined
                  }
                  loading={isLoading}
                  value={data[dataKey]}
                />
              </Flex>
              {action ? <Row style={{ marginTop: 12 }}>{action}</Row> : null}
            </Card>
          ))}
        </Space.$>
      </Section.$>
    );
  };
}
